import { useState } from 'react';
import { MemsApi } from 'src/api';

export type UserVote = 'up' | 'down' | null;

/**
 * Voting is kept working behind the scenes while the controls are hidden from
 * mems (see SHOW_MEM_VOTES), so it can be brought back without rewriting it.
 */
export const useMemVote = (memId: number, initialVoteCount: number) => {
  const [voteCount, setVoteCount] = useState(initialVoteCount);
  const [userVote, setUserVote] = useState<UserVote>(null);
  const [isVoting, setIsVoting] = useState(false);

  const vote = async (direction: Exclude<UserVote, null>) => {
    if (isVoting) return;
    setIsVoting(true);
    const previousVote = userVote;
    const previousCount = voteCount;
    const isUpvote = direction === 'up';
    const step = isUpvote ? 1 : -1;

    try {
      if (userVote === direction) {
        setUserVote(null);
        setVoteCount(c => c - step);
        setVoteCount(
          isUpvote ? await MemsApi.removeUpvote(memId) : await MemsApi.removeDownvote(memId),
        );
      } else {
        setUserVote(direction);
        setVoteCount(c => (previousVote === null ? c + step : c + step * 2));
        setVoteCount(isUpvote ? await MemsApi.upvoteMem(memId) : await MemsApi.downvoteMem(memId));
      }
    } catch {
      setUserVote(previousVote);
      setVoteCount(previousCount);
    } finally {
      setIsVoting(false);
    }
  };

  return {
    voteCount,
    userVote,
    isVoting,
    upvote: () => vote('up'),
    downvote: () => vote('down'),
  };
};
